import { useCallback, useEffect, useMemo, useRef } from "react";
import { getList } from "../services/api/Api";
import { getForm, saveFormV2 } from "../services/restAPI/endpoints/Forms";
import { Message } from "../components/BP2Components";
import { useIntl } from "react-intl";
import debounce from "lodash/debounce";

type tColumn = {
   label?: string;
   title?: string;
   techName?: string;
};

type UseRowColorHotkeyOptions<T = any> = {
   formId: string;
   listId: string;
   handleListResponse: (data: T[]) => void;
   columns: tColumn[] | null | undefined;
   dataSource: T[] | null | undefined;
   isMenuOpen?: boolean;
};

export function useRowColorHotkey<T>({
   formId,
   listId,
   handleListResponse,
   columns,
   dataSource,
   isMenuOpen,
}: UseRowColorHotkeyOptions<T>) {
   const intl = useIntl();
   const rootRef = useRef<HTMLElement | null>(null);
   const lastClickedCellRef = useRef<HTMLElement | null>(null);

   const COLOR_CYCLE = useMemo(
      () =>
         ["#C8DAf7", "#FFFF00", "#FCE5CC", "#B4D6A7", "#D8D1E8", "#E06767"].map((color) =>
            color.trim().toUpperCase(),
         ),
      [],
   );

   const rowColorKey = useMemo(() => {
      const col = (columns ?? []).find((column) => column?.techName === "row_color");
      const key = col?.label?.toLowerCase() ?? col?.title?.toLowerCase() ?? null;
      return key && key.length ? key : null;
   }, [columns]);

   const isInsideRoot = (el: Element | null) => {
      const root = rootRef.current;
      return !!root && !!el && root.contains(el);
   };

   const getFormData = async (recordId) => {
      const mainForm = await getForm(formId, recordId, undefined, null).then(
         (res) => res.data.default.mainForm,
      );
      const fields = Object.values(mainForm.forms_data.tab[0].elements);
      const formValues = {};
      Object.keys(fields).forEach((key) => {
         const currField = fields[key];
         const respValue = currField?.value;
         if (respValue?.value) {
            formValues[currField?.tech_name] = respValue?.value;
         } else {
            formValues[currField?.tech_name] = respValue;
         }
      });
      return formValues;
   };

   const getActiveRowIndex = useCallback((): number | null => {
      const root = rootRef.current;
      if (!root) return null;

      let cell = root.querySelector<HTMLElement>('[data-grid-cell="true"][tabindex="0"]') || null;

      if (!cell && root.contains(document.activeElement)) {
         const fromActive = (document.activeElement as Element)?.closest?.(
            '[data-grid-cell="true"]',
         ) as HTMLElement | null;
         if (fromActive) cell = fromActive;
      }

      if (!cell) cell = lastClickedCellRef.current;

      if (!cell) return null;
      const row = Number(cell.getAttribute("data-row"));
      return Number.isFinite(row) ? row : null;
   }, []);

   const findRowElement = useCallback((rowIndex: number): HTMLTableRowElement | null => {
      const root = rootRef.current;
      if (!root) return null;
      const cell =
         root.querySelector<HTMLElement>(`[data-grid-cell="true"][data-row="${rowIndex}"]`) ||
         root.querySelector<HTMLElement>(
            `[data-grid-cell="true"][data-row="${rowIndex}"][data-col="0"]`,
         );
      return (cell?.closest("tr") as HTMLTableRowElement | null) ?? null;
   }, []);

   const paintRow = useCallback(
      (rowIndex: number, cssColor: string | null) => {
         const tr = findRowElement(rowIndex);
         if (!tr) return;
         Array.from(tr.querySelectorAll<HTMLTableCellElement>("td")).forEach((td) => {
            td.style.backgroundColor = cssColor ?? "";
         });
      },
      [findRowElement],
   );

   const readRowColor = useCallback(
      (row: any): string | null => {
         if (!rowColorKey) return null;
         const raw = row?.[rowColorKey];
         if (raw == null) return null;
         const value = typeof raw === "object" && "value" in raw ? raw.value : raw;
         if (value == null) return null;
         const color = String(value).trim().toUpperCase();
         return color || null;
      },
      [rowColorKey],
   );

   type PendingState = {
      baseIndex: number;
      increments: number;
      rowIndex: number;
   };
   const pendingByRowRef = useRef<Map<string | number, PendingState>>(new Map());
   const debouncedCommitByRowRef = useRef<Map<string | number, ReturnType<typeof debounce>>>(
      new Map(),
   );

   const getRowKey = (row: any, rowIndex: number) =>
      row?.id ?? row?._sysRecordId ?? row?.key ?? rowIndex;

   const idxToColor = (stateIndex: number): string | null =>
      stateIndex <= 0 ? null : COLOR_CYCLE[stateIndex - 1];

   const colorToStateIndex = (hexOrNull: string | null): number => {
      if (!hexOrNull) return 0;
      const idx = COLOR_CYCLE.indexOf(hexOrNull.toUpperCase());
      return idx < 0 ? 0 : idx + 1;
   };

   useEffect(() => {
      return () => {
         debouncedCommitByRowRef.current.forEach((debounc) => debounc?.cancel?.());
         debouncedCommitByRowRef.current.clear();
         pendingByRowRef.current.clear();
      };
   }, []);

   // last active cell
   useEffect(() => {
      const root = rootRef.current;
      if (!root) return;

      const remember = (e: Event) => {
         const t = e.target as Element | null;
         const wrapper = t?.closest?.('[data-grid-cell="true"]') as HTMLElement | null;
         if (wrapper && root.contains(wrapper)) {
            lastClickedCellRef.current = wrapper;
         }
      };

      root.addEventListener("pointerdown", remember, true);
      root.addEventListener("mousedown", remember, true);
      root.addEventListener("click", remember, true);
      return () => {
         root.removeEventListener("pointerdown", remember, true);
         root.removeEventListener("mousedown", remember, true);
         root.removeEventListener("click", remember, true);
      };
   }, []);

   // set color to row
   useEffect(() => {
      if (!rowColorKey) return;
      const rows = dataSource ?? [];
      rows.forEach((row, index) => {
         const val = readRowColor(row);
         const hex = (val ?? "").toUpperCase();
         const ok = COLOR_CYCLE.includes(hex);
         paintRow(index, ok ? hex : null);
      });
   }, [dataSource, rowColorKey, COLOR_CYCLE, readRowColor, paintRow]);

   useEffect(() => {
      const root = rootRef.current;
      if (!root) return;
      if (!rowColorKey) return;

      const onKeyDown = async (e: KeyboardEvent) => {
         if (!isInsideRoot(e.target as Element)) return;
         if (isMenuOpen) return;

         if (e.key.toLowerCase() === "c" && e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const rowIndex = getActiveRowIndex();
            const rows = dataSource ?? [];
            if (rowIndex == null || rowIndex < 0 || rowIndex >= rows.length) {
               return;
            }

            const row = rows[rowIndex] as any;
            const rowKey = getRowKey(row, rowIndex);

            let pending = pendingByRowRef.current.get(rowKey);
            if (!pending) {
               const backendHex = (readRowColor(row) ?? "").toUpperCase() || null;
               const baseIndex = colorToStateIndex(backendHex);
               pending = { baseIndex, increments: 0, rowIndex };
               pendingByRowRef.current.set(rowKey, pending);
            } else {
               pending.rowIndex = rowIndex;
            }

            pending.increments += 1;

            const STATES = COLOR_CYCLE.length + 1;
            const previewStateIndex = (pending.baseIndex + pending.increments) % STATES;
            const previewColor = idxToColor(previewStateIndex);

            paintRow(rowIndex, previewColor);

            let commit = debouncedCommitByRowRef.current.get(rowKey);
            if (!commit) {
               commit = debounce(async () => {
                  const pendingState = pendingByRowRef.current.get(rowKey);
                  if (!pendingState) return;

                  const finalStateIndex =
                     (pendingState.baseIndex + pendingState.increments) % STATES;
                  const finalColor = idxToColor(finalStateIndex);

                  try {
                     const formValues = await getFormData(row.id);
                     const payload = { ...formValues, row_color: finalColor };

                     const save = await saveFormV2(formId, null, payload);
                     if (save.status === 200) {
                        await getList(listId, 1, 10).then((res) => {
                           handleListResponse(res);
                           Message.success(intl.formatMessage({ id: "global.colorIsChanged" }));
                        });
                     }
                  } catch (err) {
                     const rollback = idxToColor(pendingState.baseIndex);
                     paintRow(pendingState.rowIndex, rollback);
                     Message.error(intl.formatMessage({ id: "global.errorWhileSaving" }));
                  } finally {
                     debouncedCommitByRowRef.current.get(rowKey)?.cancel();
                     debouncedCommitByRowRef.current.delete(rowKey);
                     pendingByRowRef.current.delete(rowKey);
                  }
               }, 1000);

               debouncedCommitByRowRef.current.set(rowKey, commit);
            }

            commit();
         }
      };

      root.addEventListener("keydown", onKeyDown, true);
      return () => root.removeEventListener("keydown", onKeyDown, true);
   }, [
      rowColorKey,
      dataSource,
      isMenuOpen,
      getActiveRowIndex,
      readRowColor,
      paintRow,
      COLOR_CYCLE,
   ]);

   const getHotkeyProps = useCallback(
      () =>
         ({
            ref: (el: HTMLElement | null) => {
               rootRef.current = el;
            },
         }) as React.HTMLAttributes<HTMLElement>,
      [],
   );

   return { getHotkeyProps, rowColorKey };
}
